import { type OpenShift } from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { useToast } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { formatCentsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import { BookabilityDecision } from "@src/appV2/OpenShifts/ShiftAction/types";
import { Text } from "@src/appV2/redesign/system/ui-components";
import { useState } from "react";
import { generatePath, useHistory } from "react-router-dom";

import { useDefinedWorker } from "../../../../Worker/useDefinedWorker";
import { Button } from "../../../components/Button";
import { DialogFooter } from "../../../components/DialogFooter";
import { LoadingButton } from "../../../components/LoadingButton";
import { SHIFT_DISCOVERY_SHIFT_DOUBLE_BOOKED_MODAL_PATH } from "../../../ShiftDiscovery/paths";
import { useShiftModalsDataContext } from "../../../ShiftDiscovery/useShiftModalsDataContext";
import { WORKPLACE_QUIZ_RULES_PATH_FULL } from "../../../WorkplaceQuiz/paths";
import { performChecksBeforeBooking, PreBookingCheck } from "../../Booking/useBookShift";
import { useCheckPreBookingState } from "../../Booking/useCheckPreBookingState";
import { getBackgroundCheckContext } from "../../getBackgroundCheckContext";
import { type ModalShiftData } from "../../types";
import { useBookDoubleShifts } from "../hooks/useBookDoubleShifts";
import type { DoubleShiftPay, ShiftDouble } from "../types";
import { getDisplayableErrorMessage, isQuizOnlyBlocker } from "./bookingUtils";

interface ShiftDoubleBookingFooterProps {
  shift: ModalShiftData;
  double: ShiftDouble;
  selectedCompanion: OpenShift;
  selectedCompanionPay?: DoubleShiftPay;
  /** Offer ID for the selected companion, from freshly created offer or existing relationship. */
  selectedCompanionOfferId?: string;
  /** Whether companion offers are still being created/loaded. */
  isCompanionOffersLoading?: boolean;
  hideTotalPay?: boolean;
}

export function ShiftDoubleBookingFooter(props: ShiftDoubleBookingFooterProps) {
  const {
    shift,
    double,
    selectedCompanion,
    selectedCompanionPay,
    selectedCompanionOfferId,
    isCompanionOffersLoading = false,
    hideTotalPay = false,
  } = props;

  const history = useHistory();
  const { showErrorToast, showSuccessToast } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);

  const {
    navigateToModal,
    setInitialShiftModalsData,
    bookabilityStatus,
    companionBookabilityStatuses,
  } = useShiftModalsDataContext();

  const { missingDataTypes: missingBackgroundCheckDataTypes } =
    getBackgroundCheckContext(bookabilityStatus);

  const { geolocationIsUnavailable, deviceGeolocation, isPreBookingChecksLoading } =
    useCheckPreBookingState({
      workplaceId: shift.workplace.id,
      shiftId: shift.id,
    });

  const worker = useDefinedWorker();

  const { bookDoubleShifts, isBooking } = useBookDoubleShifts();

  const anchorOfferId = shift.offer?.id;
  const companionOfferId =
    selectedCompanionOfferId ?? selectedCompanion.relationships.offer?.data.id;

  const canBook = isDefined(anchorOfferId) && isDefined(companionOfferId);

  const anchorIsBookable =
    bookabilityStatus?.attributes.bookability.decision === BookabilityDecision.ALLOWED;
  const companionBookability = companionBookabilityStatuses?.get(selectedCompanion.id);
  const companionIsBookable =
    companionBookability?.attributes.bookability.decision === BookabilityDecision.ALLOWED;
  // Only allow booking when both anchor and companion shifts have no blockers
  const allShiftsBookable = anchorIsBookable && companionIsBookable;

  const anchorPay = double.anchorShiftPay?.totalPayInMinorUnits;
  const companionPay = selectedCompanionPay?.totalPayInMinorUnits;
  const totalPay =
    isDefined(anchorPay) && isDefined(companionPay) ? anchorPay + companionPay : undefined;

  const handleBook = async () => {
    if (!canBook) {
      return;
    }

    setIsProcessing(true);

    try {
      const shouldProceed = performChecksBeforeBooking({
        missingBackgroundCheckDataTypes,
        isBasketSizeWarningAcknowledgmentNeeded: false,
        geolocationIsUnavailable,
        requiresBreakPolicyAcknowledgement: false,
        navigateToModal,
        shiftId: shift.id,
        skipChecks: [
          PreBookingCheck.BOOKING_CONFIRMATION,
          PreBookingCheck.BASKET_SIZE_WILL_REACH_WARNING,
          PreBookingCheck.BREAK_POLICY_ACKNOWLEDGEMENT,
        ],
        shouldEducateNewWorker: false,
        shouldShowArrivalTimeModal: false,
        shouldShowClipboardScoreEducation: false,
        history,
      });

      if (!shouldProceed) {
        setIsProcessing(false);
        return;
      }

      const shiftsToBook = [
        { shiftId: shift.id, offerId: anchorOfferId },
        { shiftId: selectedCompanion.id, offerId: companionOfferId },
      ];

      const results = await bookDoubleShifts(shiftsToBook, deviceGeolocation?.geoLocation);
      const failedShiftIds = results.failedShifts.map((f) => f.shiftId);

      logEvent(APP_V2_USER_EVENTS.SHIFT_DOUBLE_BOOKED, {
        workplaceId: double.workplaceId,
        workerId: worker.userId,
        anchorShiftId: shift.id,
        companionShiftId: selectedCompanion.id,
        qualification: double.qualification,
        bookedShiftIds: results.bookedShiftIds,
        failedShiftIds,
      });

      if (results.bookedShiftIds.length === 0) {
        const errorMessage =
          results.failedShifts.length > 0
            ? getDisplayableErrorMessage(results.failedShifts[0].error)
            : "Something went wrong while booking these shifts. Please try again.";
        showErrorToast(errorMessage);
        return;
      }

      if (results.failedShifts.length > 0) {
        showSuccessToast(
          `Booked ${results.bookedShiftIds.length} of 2 shifts. One shift could not be booked.`
        );
      }

      setInitialShiftModalsData({
        shift: {
          ...shift,
          offer: shift.offer,
        },
        double: {
          ...double,
        },
        doubleBookingResults: {
          bookedShiftIds: results.bookedShiftIds,
          failedShiftIds,
        },
      });

      navigateToModal(SHIFT_DISCOVERY_SHIFT_DOUBLE_BOOKED_MODAL_PATH, {
        pathParams: { shiftId: shift.id },
      });
    } catch (error: unknown) {
      showErrorToast(getDisplayableErrorMessage(error));
    } finally {
      setIsProcessing(false);
    }
  };

  const quizIsOnlyBlocker = isQuizOnlyBlocker(bookabilityStatus, companionBookability);

  if (!allShiftsBookable && quizIsOnlyBlocker) {
    return (
      <DialogFooter withShadow orientation="vertical">
        <Stack sx={{ width: "100%", paddingX: 1, gap: 4 }}>
          <Text variant="caption" sx={{ textAlign: "center", color: "text.secondary" }}>
            Complete a quick quiz to unlock booking for both shifts
          </Text>
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={() => {
              logEvent(APP_V2_USER_EVENTS.SHIFT_DOUBLE_QUIZ_CTA_TAPPED, {
                workplaceId: double.workplaceId,
                anchorShiftId: shift.id,
                companionShiftId: selectedCompanion.id,
              });

              const basePath = generatePath(WORKPLACE_QUIZ_RULES_PATH_FULL, {
                shiftId: shift.id,
              });
              history.push(`${basePath}?fromDouble=true`);
            }}
          >
            Take quiz & book your double
          </Button>
        </Stack>
      </DialogFooter>
    );
  }

  // When companion bookability is still loading (e.g. returning from quiz), show loading button
  // instead of prematurely showing guidance text
  const isCompanionBookabilityLoading = !isDefined(companionBookabilityStatuses);

  if (!allShiftsBookable && !isCompanionBookabilityLoading) {
    return (
      <DialogFooter withShadow orientation="vertical">
        <Stack sx={{ width: "100%", paddingX: 1 }}>
          <Text variant="caption" sx={{ textAlign: "center", color: "text.secondary" }}>
            You're almost there! Tap on each shift to review and complete any open requirements,
            then come back here to book your double.
          </Text>
        </Stack>
      </DialogFooter>
    );
  }

  return (
    <DialogFooter withShadow orientation="vertical">
      <Stack sx={{ width: "100%", paddingX: 1, gap: 4 }}>
        {isDefined(totalPay) && !hideTotalPay && (
          <Stack direction="row" justifyContent="space-between" alignItems="baseline">
            <Text variant="body2">Total for both shifts</Text>
            <Text semibold variant="h6">
              {formatCentsAsUsd(totalPay)}
            </Text>
          </Stack>
        )}

        <LoadingButton
          fullWidth
          disabled={!canBook || !allShiftsBookable || isProcessing || isPreBookingChecksLoading}
          isLoading={
            isProcessing || isBooking || isCompanionBookabilityLoading || isCompanionOffersLoading
          }
          variant="brand"
          size="large"
          onClick={handleBook}
        >
          Book both shifts
        </LoadingButton>
      </Stack>
    </DialogFooter>
  );
}
